import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaGlasses } from 'react-icons/fa';
import { FiAlertTriangle } from 'react-icons/fi';
import { HiOutlineChartBar } from 'react-icons/hi2';
import { useSelector } from "react-redux";
import {
  DashboardStatus,
  getDashboardStatusColor
} from "../../../constants/integrationTypes";
import { EntityViewTooltip } from '../../../features/EntityViewTooltip/EntityViewTooltip';
import '../../../features/StrategymapSidePanel/components/StrategyMapSidePanelItem/StrategyMapSidePanelItem.css';
import { useJwt } from "../../../helpers/common";
import { useResourceInfo } from '../../../hooks/useResourceInfo';
import TooltipWrapper from '../../../shared/atoms/TooltipWrapper';
import { getEntityTypeDisplay } from '../../../utils/entityDisplayUtils';
import { hasAnyPositiveImpact } from "../../../utils/taskUtils";

interface ProjectItemProps {
  id: string;
  name: string;
  simFlag: boolean;
  status?: string;
  simStatus?: string;
  dependencies?: any[];
  isHighlighted?: boolean;
  showTimelineIcon?: boolean;
  onItemClick: () => void;
  onTimelineClick?: () => void;
  onContextMenu?: (e: React.MouseEvent, id: string) => void;
  tooltipType?: 'project' | 'task';
  projectData?: any;
  taskData?: any;
  isTemplate?: boolean;
}

const ProjectItem: React.FC<ProjectItemProps> = ({
  id,
  name,
  simFlag,
  status,
  simStatus,
  dependencies = [],
  isHighlighted = false,
  showTimelineIcon = true,
  onItemClick,
  onTimelineClick,
  onContextMenu,
  tooltipType,
  projectData,
  taskData,
  isTemplate = false
}) => {

  // Add ref to scroll highlighted item into view
  const itemRef = React.useRef<HTMLLIElement>(null);

  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [isTooltipHovered, setIsTooltipHovered] = React.useState(false);
  const showTimerRef = React.useRef<number | null>(null);
  const hideTimerRef = React.useRef<number | null>(null);
  const [arrowPos, setArrowPos] = React.useState<{ top: number; side: 'left' | 'right' }>({ top: 0, side: 'right' });
  const [isItemHovered, setIsItemHovered] = React.useState(false);
  const { getResourceName } = useResourceInfo();

  const handleItemClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setShowTooltip(false);
    onItemClick();
  };

  const handleTimelineClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTimelineClick) {
      onTimelineClick();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (onContextMenu) {
      onContextMenu(e, id);
    }
  };

  const hasPositiveImpact = React.useMemo(() => hasAnyPositiveImpact(dependencies, simFlag), [dependencies, simFlag]);

  const entityType = tooltipType === 'task' 
      ? (taskData?.integration_data?.integration_item_type || taskData?.entity_type || 'task') 
      : (projectData?.entity_type || 'project');
  const childEntityType = tooltipType === 'task' 
      ? taskData?.child_entity_type 
      : projectData?.child_entity_type; 
      

  const entityInfo = getEntityTypeDisplay(
    projectData?.entity_type ?? taskData?.entity_type,
    projectData?.type ?? taskData?.type,
  );
  /*
    if integration DATA is there

  */
  const ShowIcon = entityInfo.icon;

  useEffect(() => {
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [isHighlighted]);

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));
  const TOOLTIP_WIDTH = 300;
  const VIEWPORT_MARGIN = 12;
  const ARROW_SIZE = 12;
  const calculateTooltipPosition = React.useCallback(() => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const rightCandidate = rect.right + VIEWPORT_MARGIN;
    const placeOnRight = rightCandidate + TOOLTIP_WIDTH + VIEWPORT_MARGIN <= vw;
    const left = placeOnRight
      ? clamp(rightCandidate, VIEWPORT_MARGIN, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
      : clamp(rect.left - TOOLTIP_WIDTH - VIEWPORT_MARGIN, VIEWPORT_MARGIN, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN);

    const provisionalTop = rect.top + rect.height / 2;
    setTooltipPos({ left, top: provisionalTop });

    requestAnimationFrame(() => {
      const el = tooltipRef.current;
      if (!el) return;
      const th = el.offsetHeight || 200;
      const centeredTop = rect.top + rect.height / 2 - th / 2;
      const top = clamp(centeredTop, VIEWPORT_MARGIN, vh - th - VIEWPORT_MARGIN);
      setTooltipPos(prev => ({ ...prev, top }));

      const itemCenterY = rect.top + rect.height / 2;
      const arrowTop = clamp(itemCenterY - top - ARROW_SIZE / 2, 8, th - ARROW_SIZE - 8);
      setArrowPos({ top: arrowTop, side: placeOnRight ? 'left' : 'right' });
    });
  }, []);

  const handleMouseEnter = () => {
    setIsItemHovered(true);
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
    }
    // if (!isScrolling && !suppressTooltip) {
      showTimerRef.current = window.setTimeout(() => {
        setShowTooltip(true);
        setTimeout(calculateTooltipPosition, 0);
        showTimerRef.current = null;
      }, 250);
    // }
  };

  const handleMouseLeave = () => {
    setIsItemHovered(false);
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      if (!isTooltipHovered) {
        setShowTooltip(false);
      }
      hideTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    if (!showTooltip) return;
    const onResize = () => calculateTooltipPosition();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [showTooltip, calculateTooltipPosition]);

  useEffect(() => {
    const onScroll = () => {
      setShowTooltip(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showTooltip]);

  const statusKey = (simFlag ? (simStatus || status) : status)?.toLowerCase?.() || '';
 
  const showRiskTriangle = statusKey === DashboardStatus.AT_RISK;

  

  // Removed renderTooltipContent as it is replaced by EntityViewTooltip

  return (
    <>
      <li
        ref={itemRef}
        className={`smap-side-panel-item ${isHighlighted ? "smap-side-panel-item--highlighted" : ""}`}
        onClick={handleItemClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-center gap-1 mr-2 min-w-0 w-full">
          <div className="flex items-center relative mr-1 flex-shrink-0">
            {/* @ts-ignore */}
            <ShowIcon size={18} className={entityInfo.iconClass} />
            <span
              className={`smap-side-panel-item__status-dot ${getDashboardStatusColor(statusKey)}`}
            />
          </div>
          <span
            className={`${showRiskTriangle ? "text-red-500" : ""} smap-side-panel-item__name truncate`}
            title={
              !isTemplate && showRiskTriangle
                ? "This is contributing to risk"
                : undefined
            }
          >
            {/* {!isTemplate && showRiskTriangle && <FiAlertTriangle className="mr-1" />} */}
            {name}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {dependencies.length > 0 && (
            <div className="flex w-3 h-3">
              <img
                src={
                  hasPositiveImpact
                    ? "/configuration%20red.svg"
                    : "/configuration%20green.svg"
                }
                alt="Dependency"
                className="smap-side-panel-item__dependency-icon"
              />
            </div>
          )}

          {showRiskTriangle && (
            <TooltipWrapper message="Risk Triangle">
              <FiAlertTriangle className="smap-side-panel-item__risk-icon !text-xs" />
            </TooltipWrapper>
          )}

          {(projectData?.is_sim_pending ?? projectData?.is_sim_pending) && (
            <FaGlasses className="text-[#467966] smap-side-panel-item-icon smap-side-panel-item__risk-icon" />
          )}
        </div>
        {showTimelineIcon && onTimelineClick && (
          <div className="smap-side-panel-item__timeline-icon">
            <div className="flex gap-2 items-center rounded-full bg-gray-200 dark:bg-gray-800 px-3 py-1">
              <HiOutlineChartBar
                className="text-xs cursor-pointer"
                onClick={handleTimelineClick}
              />
            </div>
          </div>
        )}
      </li>
      {showTooltip &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            className={`z-[9999] w-[300px] shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg fixed`}
            style={{
              left: tooltipPos.left,
              top: tooltipPos.top,
            }}
            onMouseEnter={() => {
              setIsTooltipHovered(true);
              if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              setIsTooltipHovered(false);
              if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
              }
              hideTimerRef.current = window.setTimeout(() => {
                setShowTooltip(false);
                hideTimerRef.current = null;
              }, 150);
            }}
          >
            <div
              className="pointer-events-none absolute"
              style={{
                top: arrowPos.top,
                left: arrowPos.side === "left" ? -6 : undefined,
                right: arrowPos.side === "right" ? -6 : undefined,
              }}
            >
              <div className="w-3 h-3 rotate-45 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700" />
            </div>
            {/* {renderTooltipContent()} */}
            <EntityViewTooltip 
              projectData={projectData} 
              taskData={taskData}
              tooltipType={tooltipType}
              isSimulation={simFlag}
              isTemplate={isTemplate}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default ProjectItem; 